import { useEffect, useState } from "react";
import { Plus, Trash2, Edit3, Upload, X, Globe, Link2, Copy } from "lucide-react";
import StaffLayout from "@/components/staff/StaffLayout";
import { api, fmtMoney, API } from "@/lib/api";
import { LANGS } from "@/lib/i18n";
import { toast } from "sonner";

const empty = {
  name: "", description: "", short_description: "",
  price_per_night: 100, max_guests: 2, bedrooms: 1, beds: 1, bathrooms: 1, size_sqm: 25,
  amenities: [], photos: [], available: true,
  i18n: {},
  ical_import_url: "",
};

export default function StaffRoomsPage() {
  const [rooms, setRooms] = useState([]);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [activeLang, setActiveLang] = useState("en");

  function load() { api.get("/rooms").then((r) => setRooms(r.data)); }
  useEffect(load, []);

  async function save() {
    try {
      const payload = { ...editing, price_per_night: Number(editing.price_per_night) };
      if (editing.id) await api.put(`/rooms/${editing.id}`, payload);
      else await api.post("/rooms", payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  }

  async function del(id) {
    if (!confirm("Delete this room?")) return;
    await api.delete(`/rooms/${id}`);
    toast.success("Deleted");
    load();
  }

  async function bulkUpload(files) {
    setUploading(true);
    try {
      const fd = new FormData();
      for (const f of files) fd.append("files", f);
      const r = await api.post("/upload/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const urls = r.data.uploaded.map((u) => `${API}${u.url.replace(/^\/api/, "")}`);
      setEditing((e) => ({ ...e, photos: [...e.photos, ...urls] }));
      toast.success(`Uploaded ${urls.length} photo${urls.length > 1 ? "s" : ""}`);
    } catch (e) {
      toast.error("Upload failed");
    } finally { setUploading(false); }
  }

  function copyIcal(id) {
    const url = `${API}/rooms/${id}/calendar.ics`;
    navigator.clipboard.writeText(url);
    toast.success("iCal URL copied — paste into Booking.com / Airbnb sync");
  }

  function setLocalized(field, value) {
    if (activeLang === "en") {
      setEditing({ ...editing, [field]: value });
    } else {
      const i18n = { ...(editing.i18n || {}) };
      i18n[activeLang] = { ...(i18n[activeLang] || {}), [field]: value };
      setEditing({ ...editing, i18n });
    }
  }

  function getLocalized(field) {
    if (activeLang === "en") return editing[field] ?? "";
    return editing.i18n?.[activeLang]?.[field] ?? "";
  }

  return (
    <StaffLayout>
      <div className="flex justify-between items-center">
        <div>
          <h1 className="font-display text-4xl">Rooms</h1>
          <p className="text-sm text-[#71717A] mt-1">Manage all rooms, apartments and their translations.</p>
        </div>
        <button data-testid="staff-add-room" onClick={() => { setEditing({ ...empty }); setActiveLang("en"); }} className="bg-brand-primary text-white px-5 py-2.5 rounded-sm text-sm flex items-center gap-2">
          <Plus size={16} /> New room
        </button>
      </div>

      <div className="mt-8 bg-white border border-[#E4E4E7] rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-[#FAFAFA] text-[#71717A] text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left p-4">Room</th>
              <th className="text-left p-4">Price / night</th>
              <th className="text-left p-4">Capacity</th>
              <th className="text-left p-4">Available</th>
              <th className="text-left p-4">iCal</th>
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F4F4F5]">
            {rooms.map((r) => (
              <tr key={r.id} data-testid={`staff-room-row-${r.id}`}>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    {r.photos[0] && <img src={r.photos[0]} alt="" className="w-14 h-14 object-cover rounded-sm" />}
                    <div>
                      <div className="font-medium">{r.name}</div>
                      <div className="text-xs text-[#71717A] line-clamp-1">{r.short_description}</div>
                    </div>
                  </div>
                </td>
                <td className="p-4">{fmtMoney(r.price_per_night)}</td>
                <td className="p-4">{r.max_guests} guests</td>
                <td className="p-4">{r.available ? "Yes" : "No"}</td>
                <td className="p-4">
                  <button data-testid={`staff-ical-${r.id}`} onClick={() => copyIcal(r.id)} title="Copy iCal export URL" className="text-xs flex items-center gap-1 text-brand-primary hover:text-brand-primaryHover">
                    <Link2 size={13} /> Copy URL
                  </button>
                </td>
                <td className="p-4 text-right">
                  <button data-testid={`staff-edit-room-${r.id}`} onClick={() => { setEditing(r); setActiveLang("en"); }} className="p-2 hover:bg-[#FAFAFA] rounded-sm"><Edit3 size={15} /></button>
                  <button data-testid={`staff-delete-room-${r.id}`} onClick={() => del(r.id)} className="p-2 hover:bg-rose-50 text-rose-600 rounded-sm"><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
            {rooms.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[#71717A]">No rooms yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="room-editor">
          <div className="bg-white rounded-md w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-5 border-b">
              <h2 className="font-display text-2xl">{editing.id ? "Edit room" : "New room"}</h2>
              <button onClick={() => setEditing(null)} className="p-2"><X size={18} /></button>
            </div>

            {/* lang tabs */}
            <div className="px-6 pt-5 flex items-center gap-2 border-b">
              <Globe size={14} className="text-brand-muted" />
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  data-testid={`editor-lang-${l.code}`}
                  onClick={() => setActiveLang(l.code)}
                  className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm ${activeLang === l.code ? "bg-brand-primary text-white" : "text-[#71717A] hover:bg-[#FAFAFA]"}`}
                >
                  {l.flag} {l.code}
                </button>
              ))}
            </div>

            <div className="p-6 space-y-5">
              <FieldInput label={`Name (${activeLang})`} value={getLocalized("name")} onChange={(v) => setLocalized("name", v)} testId="editor-name" />
              <FieldInput label={`Short description (${activeLang})`} value={getLocalized("short_description")} onChange={(v) => setLocalized("short_description", v)} />
              <div>
                <label className="text-xs uppercase tracking-wider text-[#71717A]">Description ({activeLang})</label>
                <textarea rows={4} value={getLocalized("description")} onChange={(e) => setLocalized("description", e.target.value)} className="mt-1 w-full border border-[#E4E4E7] rounded-sm px-3 py-2 text-sm" />
              </div>

              {activeLang === "en" && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <FieldInput label="Price / night (€)" type="number" value={editing.price_per_night} onChange={(v) => setEditing({ ...editing, price_per_night: v })} testId="editor-price" />
                    <FieldInput label="Max guests" type="number" value={editing.max_guests} onChange={(v) => setEditing({ ...editing, max_guests: Number(v) })} />
                    <FieldInput label="Beds" type="number" value={editing.beds} onChange={(v) => setEditing({ ...editing, beds: Number(v) })} />
                    <FieldInput label="Size (m²)" type="number" value={editing.size_sqm || ""} onChange={(v) => setEditing({ ...editing, size_sqm: Number(v) || null })} />
                  </div>
                  <FieldInput
                    label="Amenities (comma separated)"
                    value={(editing.amenities || []).join(", ")}
                    onChange={(v) => setEditing({ ...editing, amenities: v.split(",").map((s) => s.trim()).filter(Boolean) })}
                  />

                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <label className="text-xs uppercase tracking-wider text-[#71717A]">Photos</label>
                      <label className="cursor-pointer bg-brand-secondary px-3 py-1.5 rounded-sm text-xs flex items-center gap-2">
                        <Upload size={13} /> {uploading ? "Uploading…" : "Bulk upload"}
                        <input data-testid="bulk-upload-input" type="file" multiple accept="image/*" className="hidden" onChange={(e) => bulkUpload(e.target.files)} />
                      </label>
                    </div>
                    <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                      {(editing.photos || []).map((p, i) => (
                        <div
                          key={p + i}
                          data-testid={`editor-photo-${i}`}
                          draggable
                          onDragStart={(e) => { e.dataTransfer.setData("text/plain", String(i)); }}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => {
                            e.preventDefault();
                            const from = Number(e.dataTransfer.getData("text/plain"));
                            if (Number.isNaN(from) || from === i) return;
                            const arr = [...editing.photos];
                            const [moved] = arr.splice(from, 1);
                            arr.splice(i, 0, moved);
                            setEditing({ ...editing, photos: arr });
                          }}
                          className="relative aspect-square cursor-move group"
                        >
                          <img src={p} alt="" className="w-full h-full object-cover rounded-sm pointer-events-none" />
                          {i === 0 && <div className="absolute bottom-1 left-1 bg-brand-primary text-white text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm">Cover</div>}
                          <button type="button" onClick={() => setEditing({ ...editing, photos: editing.photos.filter((_, j) => j !== i) })} className="absolute top-1 right-1 bg-white/90 rounded-full p-1 hover:bg-white opacity-0 group-hover:opacity-100">
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] text-[#71717A] mt-2">Drag to reorder. First photo is the cover.</p>
                    <FieldInput label="Or add image URL" value="" onChange={(v) => v && setEditing({ ...editing, photos: [...(editing.photos || []), v] })} placeholder="https://..." testId="editor-add-url" />
                  </div>

                  <div className="bg-[#FAFAFA] rounded-sm p-4 border border-[#E4E4E7]">
                    <div className="text-xs uppercase tracking-wider text-[#71717A] mb-2">External calendar sync (Booking.com / Airbnb iCal import)</div>
                    <FieldInput
                      label="External iCal URL (paste link from Booking.com or Airbnb)"
                      value={editing.ical_import_url || ""}
                      onChange={(v) => setEditing({ ...editing, ical_import_url: v })}
                      placeholder="https://ical.booking.com/v1/export?..."
                      testId="editor-ical-import"
                    />
                    {editing.id && (
                      <button
                        type="button"
                        data-testid="editor-ical-sync-now"
                        onClick={async () => {
                          try {
                            const r = await api.post(`/admin/rooms/${editing.id}/ical/sync`);
                            toast.success(`Synced — ${r.data.created || 0} new, ${r.data.removed || 0} removed`);
                          } catch { toast.error("Sync failed"); }
                        }}
                        className="mt-2 text-xs text-brand-primary hover:text-brand-primaryHover"
                      >Sync now</button>
                    )}
                    <p className="text-[10px] text-[#71717A] mt-2">Berdhill also syncs every 30 minutes automatically. External blocks appear in your bookings as "External (iCal)".</p>
                  </div>

                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={editing.available} onChange={(e) => setEditing({ ...editing, available: e.target.checked })} /> Available for booking
                  </label>
                </>
              )}
              {activeLang !== "en" && (
                <p className="text-xs text-[#71717A]">Only name, short description and description are translated per language. Other fields stay shared.</p>
              )}
            </div>
            <div className="p-5 border-t flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 text-sm">Cancel</button>
              <button data-testid="editor-save" onClick={save} className="bg-brand-primary text-white px-6 py-2.5 rounded-sm text-sm">Save room</button>
            </div>
          </div>
        </div>
      )}
    </StaffLayout>
  );
}

function FieldInput({ label, value, onChange, type = "text", testId, placeholder }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value ?? ""), [value]);
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-[#71717A]">{label}</span>
      <input
        data-testid={testId}
        type={type}
        value={v}
        placeholder={placeholder}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => onChange(v)}
        className="mt-1 w-full border border-[#E4E4E7] rounded-sm px-3 py-2 text-sm"
      />
    </label>
  );
}
